/**
 * VisualTransferDialog — Phase 87ax (Feb 2026)
 *
 * Vue visuelle de transfert vers un distributeur. Conçu pour la préparation
 * des tournées d'approvisionnement Conthey → distributeurs.
 *
 * Différences vs `BatchTransferDialog` (qui était une saisie ligne par ligne) :
 *  - Source pré-sélectionnée : atelier production (dépôt de fabrication par défaut
 *    selon settings.production_depot_id, sinon premier dépôt CENTRAL).
 *  - Table verticale "produit-d'abord" : 1 ligne par produit avec DLC du lot le
 *    plus ancien, total dispo, nb de lots, et 1 champ "qté à transférer".
 *  - FIFO strict côté backend : on POST le total souhaité sans `lot_id` → le
 *    backend consomme les plus vieux d'abord (cf. /transfers/batch).
 *  - Ligne dépliable pour voir le détail des lots (production_date / DLC / qty).
 *  - Indicateurs visuels : couleur DLC (rouge si < 3 j, orange si < 7 j).
 *  - Aussi utilisable comme simple "vue fabrication" sans destination saisie
 *    (les totaux par produit servent d'aide à la décision de production).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Truck,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Calendar,
  Sparkles,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import TourOrderPdf from "../TourOrderPdf";
import { PdfViewerModal } from "../PdfViewerModal";
import { useAuth } from "../../lib/auth";

// Formatte une date ISO en JJ/MM
function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Jours avant DLC (négatif si déjà passé)
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}

// Classes Tailwind selon urgence DLC
function dlcClass(iso) {
  const d = daysUntil(iso);
  if (d === null) return "text-[#A3A6A3]";
  if (d < 0) return "text-[#8B1A1A] font-semibold";
  if (d <= 2) return "text-[#C05C47] font-semibold";
  if (d <= 6) return "text-[#A36419]";
  return "text-[#1F4E3D]";
}

export default function VisualTransferDialog({ distributors, products, lots, settings, onSuccess, trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [fromDistId, setFromDistId] = useState("");
  const [toDistId, setToDistId] = useState("");
  const [qtyByProduct, setQtyByProduct] = useState({}); // { product_id: number }
  const [slotByProduct, setSlotByProduct] = useState({}); // Phase 87az : { product_id: emplacement_int }
  const [expanded, setExpanded] = useState({}); // { product_id: boolean }
  const [saving, setSaving] = useState(false);
  // ===== PDF Bon de tournée =====
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // ===== Source par défaut : production_depot_id (settings) sinon 1er central =====
  useEffect(() => {
    if (!open) return;
    if (fromDistId) return;
    const prodId = settings?.production_depot_id;
    if (prodId && distributors.some((d) => d.id === prodId)) {
      setFromDistId(prodId);
      return;
    }
    const central = distributors.find((d) => d.type === "CENTRAL");
    if (central) setFromDistId(central.id);
    else if (distributors.length > 0) setFromDistId(distributors[0].id);
  }, [open, settings, distributors, fromDistId]);

  // Reset à la fermeture
  useEffect(() => {
    if (!open) {
      setFromDistId("");
      setToDistId("");
      setQtyByProduct({});
      setSlotByProduct({});
      setExpanded({});
    }
  }, [open]);

  // Phase 87az — Quand la destination change, pré-remplir les slots par produit
  // depuis `displayed_product_rails` du distributeur cible (mapping product_id → rail).
  // Le label rail peut être "E03", "3", "A1", "12"… on extrait les chiffres pour
  // obtenir un int. Si pas de rail configuré pour un produit, le slot reste vide
  // et l'utilisateur le saisit à la main.
  useEffect(() => {
    if (!toDistId) {
      setSlotByProduct({});
      return;
    }
    const dest = distributors.find((d) => d.id === toDistId);
    const rails = dest?.displayed_product_rails || {};
    const next = {};
    for (const [pid, rail] of Object.entries(rails)) {
      const digits = String(rail || "").match(/\d+/);
      if (digits) next[pid] = Number(digits[0]);
    }
    setSlotByProduct(next);
  }, [toDistId, distributors]);

  const distMap = useMemo(
    () => Object.fromEntries(distributors.map((d) => [d.id, d])),
    [distributors]
  );
  const productMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );

  // Lots du dépôt source, regroupés par produit, triés DLC asc
  const productRows = useMemo(() => {
    if (!fromDistId) return [];
    const grouped = {};
    for (const lot of lots) {
      if (lot.distributor_id !== fromDistId) continue;
      const q = lot.quantity || 0;
      if (q <= 0) continue;
      const pid = lot.product_id;
      if (!grouped[pid]) grouped[pid] = [];
      grouped[pid].push(lot);
    }
    const rows = Object.entries(grouped).map(([pid, plots]) => {
      const sortedLots = [...plots].sort((a, b) => {
        const da = a.dlc || "9999-12-31";
        const dbb = b.dlc || "9999-12-31";
        return String(da).localeCompare(String(dbb));
      });
      const total = sortedLots.reduce((s, l) => s + (l.quantity || 0), 0);
      const oldestDlc = sortedLots[0]?.dlc || null;
      const product = productMap[pid] || { name: pid.slice(0, 8), category: "—" };
      return {
        product_id: pid,
        product,
        total,
        oldestDlc,
        lots: sortedLots,
        lotsCount: sortedLots.length,
      };
    });
    // Tri principal : par DLC oldest asc (urgent d'abord), puis nom
    rows.sort((a, b) => {
      const da = a.oldestDlc || "9999-12-31";
      const dbb = b.oldestDlc || "9999-12-31";
      const cmp = String(da).localeCompare(String(dbb));
      if (cmp !== 0) return cmp;
      return (a.product.name || "").localeCompare(b.product.name || "");
    });
    return rows;
  }, [lots, fromDistId, productMap]);

  const destinationDist = toDistId ? distMap[toDistId] : null;
  const destIsClient = destinationDist?.type === "CLIENT";

  // Lignes à transférer : { product_id, quantity, slot } (uniquement celles avec qty > 0)
  const selectedLines = useMemo(() => {
    return productRows
      .map((row) => ({ row, qty: Number(qtyByProduct[row.product_id]) || 0 }))
      .filter(({ qty }) => qty > 0)
      .map(({ row, qty }) => ({
        product_id: row.product_id,
        product_name: row.product.name,
        quantity: qty,
        available: row.total,
        slot: slotByProduct[row.product_id] || null,
      }));
  }, [productRows, qtyByProduct, slotByProduct]);

  const totalQty = selectedLines.reduce((s, l) => s + l.quantity, 0);

  // Validation — Phase 87az : slot requis par ligne (sauf destination CLIENT)
  const validationErrors = useMemo(() => {
    const errs = [];
    if (!fromDistId) errs.push("Dépôt source requis");
    if (!toDistId) errs.push("Dépôt destination requis");
    if (fromDistId && fromDistId === toDistId) errs.push("Source et destination identiques");
    if (selectedLines.length === 0) errs.push("Saisissez au moins une quantité > 0");
    if (!destIsClient) {
      for (const l of selectedLines) {
        if (!l.slot) errs.push(`Slot manquant pour « ${l.product_name} »`);
      }
    }
    for (const l of selectedLines) {
      if (l.quantity > l.available) {
        errs.push(`« ${l.product_name} » : ${l.quantity} demandées, ${l.available} dispos`);
      }
    }
    return errs;
  }, [fromDistId, toDistId, destIsClient, selectedLines]);

  const setQty = (pid, val) => {
    const v = Math.max(0, Math.floor(Number(val) || 0));
    setQtyByProduct((prev) => ({ ...prev, [pid]: v }));
  };

  const incQty = (pid, delta) => {
    setQtyByProduct((prev) => {
      const cur = Number(prev[pid]) || 0;
      return { ...prev, [pid]: Math.max(0, cur + delta) };
    });
  };

  const fillAllFifo = (pid, total) => {
    setQtyByProduct((prev) => ({ ...prev, [pid]: total }));
  };

  const clearAll = () => setQtyByProduct({});

  const toggleExpand = (pid) => {
    setExpanded((prev) => ({ ...prev, [pid]: !prev[pid] }));
  };

  const submit = async () => {
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }
    setSaving(true);
    try {
      // Snapshot des lots à consommer côté front (le backend en consomme certains
      // jusqu'à épuisement, on en a besoin AVANT pour le PDF puisque les lots
      // mutent après le POST).
      const planByProduct = {}; // pid → [{lot, qty}]
      for (const line of selectedLines) {
        let remaining = line.quantity;
        const productLots = lots
          .filter((l) => l.product_id === line.product_id && l.distributor_id === fromDistId && (l.quantity || 0) > 0)
          .sort((a, b) => String(a.dlc || "9999").localeCompare(String(b.dlc || "9999")));
        const consumed = [];
        for (const l of productLots) {
          if (remaining <= 0) break;
          const take = Math.min(remaining, l.quantity || 0);
          if (take > 0) {
            consumed.push({ lot: l, qty: take });
            remaining -= take;
          }
        }
        planByProduct[line.product_id] = consumed;
      }

      const cleanLines = selectedLines.map((l) => ({
        product_id: l.product_id,
        quantity: l.quantity,
        // lot_id null → backend applique FIFO strict (le plus vieux d'abord)
        lot_id: null,
        // Phase 87az — slot par ligne (mapping displayed_product_rails du distributeur cible)
        to_emplacement_number: destIsClient ? null : l.slot,
      }));
      const { data } = await api.post("/transfers/batch", {
        from_distributor_id: fromDistId,
        to_distributor_id: toDistId,
        // Slot global : null (chaque ligne porte son propre slot maintenant)
        to_emplacement_number: null,
        lines: cleanLines,
        reason: "Approvisionnement visuel FIFO",
        generate_bl: false,
      });
      if (data.errors_count > 0) {
        toast.warning(`${data.transfers_count} transferts OK, ${data.errors_count} erreurs — vérifiez le détail`);
      } else {
        toast.success(`${data.transfers_count} transferts effectués (${data.total_quantity} barquettes)`);
      }

      // ===== Génération du PDF Bon de tournée =====
      setPdfLoading(true);
      const sourceDoc = distMap[fromDistId];
      const destDoc = distMap[toDistId];
      const ref = `BTRN-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}-${String(Date.now()).slice(-4)}`;
      const pdfLines = selectedLines.map((l) => {
        const consumed = planByProduct[l.product_id] || [];
        return {
          product_name: productMap[l.product_id]?.name || "—",
          product_sub: productMap[l.product_id]?.sku || "",
          total_qty: l.quantity,
          slot: destIsClient ? null : l.slot,
          lots: consumed.map(({ lot, qty }) => ({
            lot_number: lot.lot_number || lot.id.slice(0, 8),
            dlc: lot.dlc,
            production_date: lot.production_date,
            qty,
          })),
        };
      });
      const fname = `${ref}_${(destDoc?.name || "tournee").replace(/[^a-zA-Z0-9_-]+/g, "_")}.pdf`;
      const blob = await pdf(
        <TourOrderPdf
          reference={ref}
          date={new Date().toISOString()}
          operator={user ? { name: user.full_name || user.email, email: user.email } : null}
          source={sourceDoc ? { name: sourceDoc.name, type: sourceDoc.type } : null}
          destination={destDoc ? {
            name: destDoc.name,
            type: destDoc.type,
            address: destDoc.address,
            city: destDoc.city,
          } : null}
          emplacement={null /* Phase 87az : slot par produit dans `lines` */}
          lines={pdfLines}
          reason="Approvisionnement visuel FIFO (préparation tournée)"
        />
      ).toBlob();
      setPdfBlob(blob);
      setPdfFilename(fname);
      setPdfLoading(false);
      setPdfOpen(true);

      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      setPdfLoading(false);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) && detail[0]?.msg ? detail[0].msg : "Erreur lors du transfert";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="open-visual-transfer-btn">
            <Truck className="w-4 h-4 mr-2" /> Approvisionner (visuel FIFO)
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="visual-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Truck className="w-5 h-5 text-[#1F4E3D]" /> Approvisionnement visuel — FIFO strict (DLC la plus ancienne d&apos;abord)
          </DialogTitle>
          <DialogDescription>
            Préparation de tournée : visualisez le stock de l&apos;atelier groupé par produit (DLC affichée), saisissez la quantité à transférer
            et validez. Le backend choisit automatiquement les lots les plus anciens en premier. Pratique sans lecteur de code-barres.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Source / destination — Phase 87az : slot par produit (plus de slot global) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#F4F4F0] border border-[#E5E5E0] rounded p-3">
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wider text-[#575A57]">Source</Label>
              <Select value={fromDistId} onValueChange={setFromDistId}>
                <SelectTrigger data-testid="visual-from-dist">
                  <SelectValue placeholder="Choisir l'atelier source" />
                </SelectTrigger>
                <SelectContent>
                  {distributors.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      <span className="text-xs text-[#A3A6A3] mr-1">[{d.type}]</span>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wider text-[#575A57]">Destination *</Label>
              <Select value={toDistId} onValueChange={setToDistId}>
                <SelectTrigger data-testid="visual-to-dist">
                  <SelectValue placeholder="Choisir le distributeur" />
                </SelectTrigger>
                <SelectContent>
                  {distributors
                    .filter((d) => d.id !== fromDistId)
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        <span className="text-xs text-[#A3A6A3] mr-1">[{d.type}]</span>
                        {d.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Astuce UX : un seul bon de tournée par destination, multi-produits */}
          {toDistId && !destIsClient && (
            <div className="bg-[#FBF4E9] border border-[#D4B776] rounded px-3 py-2 text-xs text-[#5A3F12] flex items-center gap-2" data-testid="visual-multi-hint">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                <strong>Sélectionnez tous les produits</strong> de la tournée avant de finaliser — <strong>un seul PDF</strong> sera généré pour l&apos;ensemble. Le slot de chaque produit est pré-rempli depuis la grille du distributeur (modifiable par ligne).
              </span>
            </div>
          )}

          {/* Tableau visuel par produit */}
          <div className="border border-[#E5E5E0] rounded overflow-hidden">
            <div className="bg-[#1F4E3D] text-white text-[10px] uppercase tracking-wider grid grid-cols-12 gap-2 px-3 py-2 font-medium">
              <div className="col-span-3">Produit</div>
              <div className="col-span-1 text-right">Dispo</div>
              <div className="col-span-2">DLC + courte</div>
              <div className="col-span-1 text-center">Lots</div>
              <div className="col-span-3 text-center">Qté à transférer</div>
              <div className="col-span-2 text-center">Slot cible</div>
            </div>

            {!fromDistId && (
              <div className="p-6 text-center text-sm text-[#A3A6A3]">
                Sélectionnez d&apos;abord un dépôt source.
              </div>
            )}

            {fromDistId && productRows.length === 0 && (
              <div className="p-6 text-center text-sm text-[#A3A6A3]" data-testid="visual-empty">
                Aucun produit en stock dans ce dépôt.
              </div>
            )}

            <div className="divide-y divide-[#E5E5E0] max-h-[55vh] overflow-y-auto" data-testid="visual-rows">
              {productRows.map((row) => {
                const isExpanded = !!expanded[row.product_id];
                const currentQty = Number(qtyByProduct[row.product_id]) || 0;
                const overflow = currentQty > row.total;
                return (
                  <div key={row.product_id} className={currentQty > 0 ? "bg-[#FAFAF7]" : "bg-white"} data-testid={`visual-row-${row.product_id}`}>
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                      <button
                        type="button"
                        onClick={() => toggleExpand(row.product_id)}
                        className="col-span-3 flex items-center gap-1.5 text-sm text-left hover:text-[#1F4E3D]"
                        data-testid={`visual-toggle-${row.product_id}`}
                      >
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        <span className="font-medium truncate">{row.product.name}</span>
                      </button>
                      <div className="col-span-1 text-right font-mono text-sm" data-testid={`visual-total-${row.product_id}`}>
                        {row.total}
                      </div>
                      <div className={`col-span-2 text-sm flex items-center gap-1 ${dlcClass(row.oldestDlc)}`}>
                        <Calendar className="w-3 h-3" />
                        {fmtDateShort(row.oldestDlc)}
                        {(() => {
                          const d = daysUntil(row.oldestDlc);
                          if (d === null) return null;
                          return <span className="text-[10px] ml-1">(J{d >= 0 ? "+" : ""}{d})</span>;
                        })()}
                      </div>
                      <div className="col-span-1 text-center text-xs text-[#575A57]">{row.lotsCount}</div>
                      <div className="col-span-3 flex items-center gap-1 justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => incQty(row.product_id, -1)}
                          disabled={currentQty === 0}
                          data-testid={`visual-dec-${row.product_id}`}
                        >
                          −
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          max={row.total}
                          value={currentQty || ""}
                          onChange={(e) => setQty(row.product_id, e.target.value)}
                          className={`w-16 text-center font-mono h-8 ${overflow ? "border-[#C05C47] text-[#C05C47]" : ""}`}
                          placeholder="0"
                          data-testid={`visual-qty-${row.product_id}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => incQty(row.product_id, 1)}
                          disabled={currentQty >= row.total}
                          data-testid={`visual-inc-${row.product_id}`}
                        >
                          +
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-[10px] px-1.5 h-7"
                          onClick={() => fillAllFifo(row.product_id, row.total)}
                          title="Prendre tout le stock dispo"
                          data-testid={`visual-fillall-${row.product_id}`}
                        >
                          MAX
                        </Button>
                      </div>
                      {/* Phase 87az — slot cible par ligne (pré-rempli depuis displayed_product_rails) */}
                      <div className="col-span-2 flex items-center justify-center">
                        {destIsClient ? (
                          <span className="text-[10px] text-[#A3A6A3]">livraison</span>
                        ) : !toDistId ? (
                          <span className="text-[10px] text-[#A3A6A3]">—</span>
                        ) : (
                          <Select
                            value={slotByProduct[row.product_id] ? String(slotByProduct[row.product_id]) : ""}
                            onValueChange={(v) => setSlotByProduct((p) => ({ ...p, [row.product_id]: Number(v) }))}
                          >
                            <SelectTrigger
                              className={`h-7 w-20 text-xs font-mono ${currentQty > 0 && !slotByProduct[row.product_id] ? "border-[#C05C47] text-[#C05C47]" : ""}`}
                              data-testid={`visual-slot-${row.product_id}`}
                            >
                              <SelectValue placeholder="E…" />
                            </SelectTrigger>
                            <SelectContent>
                              {(destinationDist?.emplacements || []).map((emp) => (
                                <SelectItem key={emp} value={String(emp)}>
                                  E{emp}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* Lots détaillés (expandable) */}
                    {isExpanded && (
                      <div className="px-9 pb-3 -mt-1">
                        <div className="bg-white border border-[#E5E5E0] rounded text-xs" data-testid={`visual-lots-${row.product_id}`}>
                          <div className="grid grid-cols-12 gap-2 px-2 py-1 border-b border-[#E5E5E0] text-[10px] uppercase tracking-wider text-[#A3A6A3]">
                            <div className="col-span-3">N° lot</div>
                            <div className="col-span-3">Prod. le</div>
                            <div className="col-span-3">DLC</div>
                            <div className="col-span-2 text-right">Qté</div>
                            <div className="col-span-1"></div>
                          </div>
                          {row.lots.map((l, idx) => (
                            <div key={l.id} className={`grid grid-cols-12 gap-2 px-2 py-1 ${idx === 0 ? "bg-[#FBF4E9]" : ""}`}>
                              <div className="col-span-3 font-mono">{l.lot_number || l.id.slice(0, 8)}</div>
                              <div className="col-span-3">{fmtDateShort(l.production_date)}</div>
                              <div className={`col-span-3 ${dlcClass(l.dlc)}`}>{fmtDateShort(l.dlc)}</div>
                              <div className="col-span-2 text-right font-mono">{l.quantity}</div>
                              <div className="col-span-1 text-right">
                                {idx === 0 && <Sparkles className="w-3 h-3 text-[#A36419] inline" title="Sera consommé en premier (FIFO)" />}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Récap + erreurs */}
          {validationErrors.length > 0 ? (
            <div className="bg-[#FBEEEA] border border-[#E8B0A0] rounded p-3 text-sm" data-testid="visual-validation-errors">
              <div className="flex items-center gap-2 text-[#A04E2C] font-medium mb-1">
                <AlertCircle className="w-4 h-4" /> Corrigez avant de valider :
              </div>
              <ul className="list-disc list-inside text-xs text-[#A04E2C] space-y-0.5">
                {validationErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="bg-[#E8F2EC] border border-[#A8C9B6] rounded p-3 text-sm flex items-center justify-between" data-testid="visual-validation-ok">
              <div className="flex items-center gap-2 text-[#1F4E3D]">
                <CheckCircle2 className="w-4 h-4" />
                <span>
                  Prêt à transférer <strong>{totalQty}</strong> barquette{totalQty > 1 ? "s" : ""} sur <strong>{selectedLines.length}</strong> produit{selectedLines.length > 1 ? "s" : ""}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="text-xs"
                data-testid="visual-clear-all"
              >
                Tout effacer
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-[#E5E5E0] pt-3 mt-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving || validationErrors.length > 0}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="visual-submit-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Finaliser & imprimer le bon ({totalQty} barq · {selectedLines.length} prod.)
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Aperçu PDF Bon de tournée (modal full-screen au-dessus du dialog) */}
      <PdfViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        blob={pdfBlob}
        filename={pdfFilename}
        loading={pdfLoading}
        title="Bon de tournée — Préparation approvisionnement"
      />
    </Dialog>
  );
}
